import React from 'react';
import { Badge, Card } from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type { CaseMixData } from '../types/codingData.types';
import { getComorbidityInfo } from '../utils/caseMix';

interface CaseMixCardProps {
  caseMixData: CaseMixData;
  isRecalculating?: boolean;
}

const CaseMixCard: React.FC<CaseMixCardProps> = ({
  caseMixData,
  isRecalculating = false,
}) => {
  const comorbidityInfo = getComorbidityInfo(
    caseMixData.comorbidity_adjustment
  );

  return (
    <div className='relative'>
      {/* Loading Overlay */}
      {isRecalculating && (
        <div className='absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg'>
          <div className='flex items-center gap-3 text-primary-600'>
            <Loader2 className='w-6 h-6 animate-spin' />
            <span className='text-sm font-semibold'>Recalculating...</span>
          </div>
        </div>
      )}

      <div className='grid grid-cols-12 gap-4'>
        {/* First Row */}
        {/* Payout Weight - Most important (4 cols) */}
        <Card className='col-span-12 md:col-span-4'>
          <div className='text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3'>
            Payout Weight
          </div>
          <div className='text-2xl font-bold text-primary-600 font-mono mb-1'>
            {caseMixData.case_mix_weight.toFixed(4)}
          </div>
        </Card>

        {/* HIPPS Code (2 cols) */}
        <Card className='col-span-6 md:col-span-2'>
          <div className='text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3'>
            HIPPS Code
          </div>
          <div className='px-2 py-1.5 rounded-md bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200'>
            <div className='text-base font-bold text-primary-600 font-mono text-center'>
              {caseMixData.hipps}
            </div>
          </div>
        </Card>

        {/* Comorbidity Adjustment (3 cols) */}
        <Card className='col-span-6 md:col-span-3'>
          <div className='text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3'>
            Comorbidity Adj.
          </div>
          <Badge
            className={`${comorbidityInfo.bgClass} text-white font-bold w-full h-8  1.5 flex items-center justify-center text-sm rounded-md`}
          >
            {comorbidityInfo.label}
          </Badge>
        </Card>

        {/* LUPA Threshold (3 cols) */}
        <Card className='col-span-12 md:col-span-3'>
          <div className='text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3'>
            LUPA Threshold
          </div>
          <div className='px-2 py-1.5 rounded-md bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200'>
            <div className='text-base font-bold text-primary-600 font-mono text-center'>
              {caseMixData.lupa_threshold}
            </div>
          </div>
        </Card>

        {/* Second Row */}
        {/* Clinical Group & Function Level (6 cols) */}
        <Card className='col-span-12 md:col-span-6'>
          <div className='text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3'>
            Clinical Group & Function Level
          </div>
          <div className='text-sm font-semibold text-gray-700 leading-tight'>
            {caseMixData.clinical_group_func_level}
          </div>
        </Card>

        {/* Admission Source & Timing (6 cols) */}
        <Card className='col-span-12 md:col-span-6'>
          <div className='text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3'>
            Admission Source & Timing
          </div>
          <div className='text-sm font-semibold text-gray-700 leading-tight'>
            {caseMixData.admission_source_timing}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CaseMixCard;
